import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Button,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { useAuth } from '../auth';
import { baseBackendUrl } from '../service/baseService';

interface EmailCampaignSummary {
  id: number;
  subject: string;
  templateName: string;
  mailingListName: string | null;
  sentBy: string | null;
  createdAt: string;
  recipientCount: number;
  sentCount: number;
  failedCount: number;
  openedCount: number;
  openRate: number;
}

interface EmailMessageDetail {
  id: number;
  emailAddress: string;
  status: 'Queued' | 'Sent' | 'Failed' | 'Bounced' | 'Complained';
  errorMessage: string | null;
  sesMessageId: string | null;
  queuedAt: string;
  sentAt: string | null;
  openCount: number;
  firstOpenedAt: string | null;
  lastOpenedAt: string | null;
}

interface EmailCampaignDetailResponse {
  campaign: EmailCampaignSummary;
  recipients: EmailMessageDetail[];
}

const StatTile: React.FC<{ label: string; value: string; accent?: string }> = ({ label, value, accent }) => (
  <Paper variant="outlined" sx={{ p: 2, flex: '1 1 160px', minWidth: 160 }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="h4" sx={{ mt: 0.5, color: accent }}>{value}</Typography>
  </Paper>
);

const statusColor = (status: EmailMessageDetail['status']) => {
  switch (status) {
    case 'Sent': return 'success';
    case 'Failed': return 'error';
    case 'Bounced': return 'error';
    case 'Complained': return 'warning';
    default: return 'default';
  }
};

const EmailCampaignDetailPage: React.FC = () => {
  const { IsAdmin, cookies } = useContext(useAuth);
  const navigate = useNavigate();
  const { campaignId } = useParams<{ campaignId: string }>();
  const [data, setData] = useState<EmailCampaignDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchDetail = async () => {
      if (!cookies?.['UserAuth']?.token) {
        setError('Authentication token not found');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${baseBackendUrl}/api/email/analytics/campaigns/${campaignId}`, {
          headers: { 'Authorization': `Bearer ${cookies['UserAuth'].token}` },
        });
        if (response.status === 404) {
          setError('Campaign not found');
          return;
        }
        if (!response.ok) throw new Error('Failed to fetch campaign detail');
        const result: EmailCampaignDetailResponse = await response.json();
        setData(result);
      } catch (err) {
        setError('Failed to load campaign detail');
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [cookies, campaignId]);

  if (!IsAdmin) {
    return (
      <Box sx={{ p: 3, pt: 10, textAlign: 'center' }}>
        <Typography variant="h5" color="error">Access Denied</Typography>
        <Typography>You do not have permission to access this page.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, pt: 10, maxWidth: 1200, margin: '0 auto' }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/email-analytics')} sx={{ mb: 2 }}>
        Back to Email Analytics
      </Button>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : data ? (
        <>
          <Typography variant="h4" gutterBottom>{data.campaign.subject}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Sent {format(new Date(data.campaign.createdAt), 'MMM d, yyyy h:mm a')}
            {data.campaign.mailingListName ? ` to "${data.campaign.mailingListName}"` : ''}
            {data.campaign.sentBy ? ` by ${data.campaign.sentBy}` : ''}
          </Typography>

          <Stack direction="row" spacing={2} sx={{ mb: 4, flexWrap: 'wrap', rowGap: 2 }}>
            <StatTile label="Recipients" value={data.campaign.recipientCount.toString()} />
            <StatTile label="Sent" value={data.campaign.sentCount.toString()} />
            <StatTile label="Opened" value={data.campaign.openedCount.toString()} />
            <StatTile label="Open Rate" value={`${data.campaign.openRate}%`} />
            <StatTile
              label="Errors"
              value={data.campaign.failedCount.toString()}
              accent={data.campaign.failedCount > 0 ? '#d32f2f' : undefined}
            />
          </Stack>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Recipient</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Sent At</TableCell>
                  <TableCell align="right">Opens</TableCell>
                  <TableCell>First Opened</TableCell>
                  <TableCell>Last Opened</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.recipients.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{r.emailAddress}</TableCell>
                    <TableCell>
                      {r.status === 'Failed' && r.errorMessage ? (
                        <Tooltip title={r.errorMessage}>
                          <Chip label={r.status} color={statusColor(r.status)} size="small" />
                        </Tooltip>
                      ) : (
                        <Chip label={r.status} color={statusColor(r.status)} size="small" />
                      )}
                    </TableCell>
                    <TableCell>{r.sentAt ? format(new Date(r.sentAt), 'MMM d, h:mm a') : '—'}</TableCell>
                    <TableCell align="right">{r.openCount}</TableCell>
                    <TableCell>{r.firstOpenedAt ? format(new Date(r.firstOpenedAt), 'MMM d, h:mm a') : '—'}</TableCell>
                    <TableCell>{r.lastOpenedAt ? format(new Date(r.lastOpenedAt), 'MMM d, h:mm a') : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : null}
    </Box>
  );
};

export default EmailCampaignDetailPage;
